/**
 * Helpers for the public onboarding pages: applying, activating, signing in.
 */

const TENANT_ID = /^[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$/;

/**
 * What the sign-in form starts with. Activation hands over the tenant id and
 * username in the router state; the "account active" email links to
 * /login?tenant=<id>. Only the tenant id travels in a URL: a username can be an
 * email address, which does not belong in a link.
 */
export function loginPrefill(state, search = '') {
  const fromLink = new URLSearchParams(search).get('tenant');
  const candidates = [state?.tenantId, fromLink];
  const tenantId = candidates
    .map((t) => (typeof t === 'string' ? t.trim().toLowerCase() : ''))
    .find((t) => TENANT_ID.test(t)) || '';
  const username = typeof state?.username === 'string' ? state.username.trim() : '';
  return { tenantId, username };
}

/** The fields an applicant can revise when AfricaNenda asks for changes. */
const REVISABLE = ['orgLegalName', 'displayName', 'country', 'website', 'proposedTenantId',
  'proposedFspId', 'intendedUse', 'contactName'];

/** The revision form's starting values: the application as it stands. */
export function revisionFrom(app) {
  const f = Object.fromEntries(REVISABLE.map((k) => [k, app?.[k] ?? '']));
  f.corridors = Array.isArray(app?.corridors) ? [...app.corridors] : [];
  return f;
}

/**
 * What still stops the revision being sent, in the applicant's words; empty
 * when it can go. `avail` is the live availability answer for the identifiers.
 */
export function revisionProblems(f, avail) {
  const problems = [];
  if (!f.orgLegalName?.trim()) problems.push('Enter the organisation legal name.');
  if (!f.displayName?.trim()) problems.push('Enter a display name.');
  const tenantId = f.proposedTenantId || '';
  const fspId = f.proposedFspId?.trim() || '';
  const current = avail && avail.tenantId === tenantId && (avail.fspId || '').trim() === fspId;
  if (!TENANT_ID.test(tenantId)) {
    problems.push('The tenant id must be 3 to 50 lowercase letters, numbers and hyphens.');
  } else if (!current) {
    problems.push('Checking the tenant id and FSP id…');
  } else if (!avail.tenantIdAvailable) {
    problems.push('That tenant id is already taken.');
  }
  if (!fspId) problems.push('Enter an FSP id.');
  else if (current && !avail.fspIdAvailable) problems.push('That FSP id is already taken.');
  if (!f.corridors?.length) problems.push('Choose at least one corridor.');
  if (!f.contactName?.trim()) problems.push('Enter the primary contact name.');
  return problems;
}

/**
 * The resubmission body. The engine validates it as a full application, so it
 * carries the contact email unchanged (changing it would need verifying again)
 * and the terms acceptance the applicant already gave.
 */
export function resubmitPayload(app, f) {
  const trim = (v) => (typeof v === 'string' ? v.trim() : v);
  return {
    orgLegalName: trim(f.orgLegalName),
    displayName: trim(f.displayName),
    country: trim(f.country)?.toUpperCase().slice(0, 2),
    website: trim(f.website),
    proposedTenantId: trim(f.proposedTenantId)?.toLowerCase(),
    proposedFspId: trim(f.proposedFspId),
    corridors: [...(f.corridors || [])],
    intendedUse: trim(f.intendedUse),
    contactName: trim(f.contactName),
    contactEmail: app.contactEmail,
    termsAccepted: true,
    termsVersion: app.termsVersion,
  };
}

/** The first empty sign-in field, which is where the cursor should start. */
export function firstEmptyLoginField({ tenantId, username }, multiTenant) {
  if (multiTenant && !tenantId) return 'tenantId';
  if (!username) return 'username';
  return 'password';
}
